import React from 'react'

import Icon from '@ant-design/icons/es/components/Icon'
import { makeStyles } from '@material-ui/styles'

import { ArrowIcon } from '../../icons'
import { them } from '../../them'

/** 主题颜色 */
const color = {
  primary: {
    backgroundColor: '#6791ff',
    boxShadow: '0 12px 14px 0 rgba(40, 97, 246, .38)'
  },
  warn: {
    backgroundColor: them['color-warn-default'],
    boxShadow: '0 12px 14px 0 rgba(255, 176, 51, .42)'
  }
}

export interface ProgressBarProps {
  data: (string | { text: string; type?: 'primary' | 'warn' })[];
  className?: string;
}

export const ProgressBar = ({ data, className }: ProgressBarProps): JSX.Element => {
  const { length } = data

  /** 计算单条虚线基本宽度 34为圆的宽度 15为箭头宽度 18为最后的圆点宽度 */
  const baseWidth = `(100% - ${length * 34 - (length - 1) * 15 - 18}px) / ${length * 2 - 1}`

  const classes = makeStyles({
    icon: {
      position: 'relative',
      display: 'flex',
      margin: 0,
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 0,

      '&::after': {
        position: 'absolute',
        zIndex: -1,
        left: 0,
        width: '100%',
        height: 0,
        content: '""',
        opacity: 0.2,
        borderBottom: `2px dashed ${them['color-primary-default']}`
      },

      '& > li:not(:last-child)': {
        ...color.primary, /** 默认为蓝色主题 */
        width: 34,
        height: 34,
        borderRadius: '50%',
        fontSize: them['font-size-big-default'],
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      },

      '& > li:last-child': {
        width: 14,
        height: 14,
        borderRadius: '50%',
        color: 'transparent',
        border: '2px solid rgb(40, 97, 246, 0.2)',
        background: 'white'
      }
    },

    content: {
      display: 'flex',
      margin: `calc(${them['padding-lg']} / 2) 0 0 0`,
      padding: 0,
      listStyle: 'none',
      color: them['color-default-default'],

      '& > li': {
        boxSizing: 'border-box',

        '&:not(:last-child)': {
          paddingRight: `calc(${baseWidth} / 2)`,
          flex: 1
        },

        '&:last-child': {
          paddingRight: 18,
          width: `calc(${baseWidth} + 34px)`
        }
      }
    }
  })()

  return (
    <section className={className}>
      <ul className={classes.icon}>
        {
          new Array(length * 2).fill(0).map((_, key) => {
            const isLastLi = key === length * 2 - 1
            const record = data[key / 2 >> 0]
            const inlineStyle = typeof record === 'string' || record.type === undefined || isLastLi ? {} : color[record.type]

            return key % 2 === 0 || isLastLi
              ? (
                <li key={key} style={inlineStyle}>
                  {key / 2 + 1}
                </li>
              )
              : (
                <Icon key={key} component={ArrowIcon} />
              )
          })
        }
      </ul>
      <ul className={classes.content}>
        {
          data.map((item, key) => (
            <li key={key}>
              {typeof item === 'string' ? item : item.text}
            </li>
          ))
        }
      </ul>
    </section>
  )
}
